import { useFontScale, FONT_MIN, FONT_MAX, FONT_STEP } from "@/lib/useFontScale";
import { Button } from "@/components/ui/button";
import { TextAa, Minus, Plus, ArrowCounterClockwise } from "@phosphor-icons/react";

const PRESETS = [
  { value: 0.90, label: "Compact" },
  { value: 1.00, label: "Default" },
  { value: 1.10, label: "Comfortable" },
  { value: 1.20, label: "Large" },
];

/**
 * Reading-size panel for the Settings page. Managing Principals and Partners
 * can dial the whole app's typography up or down without touching a stylesheet.
 * Persists in localStorage (per-device); does not sync to the server so each
 * partner tunes their own view.
 */
export default function FontScaleCard() {
  const { scale, setScale, increase, decrease, reset, syncedToServer } = useFontScale();
  const pct = Math.round(scale * 100);

  return (
    <div className="border border-border bg-card p-6 md:col-span-2" data-testid="font-scale-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <TextAa size={12} weight="bold" /> Reading size
          </div>
          <div className="mt-1 text-sm text-muted-foreground max-w-md">
            Scales every text element in YadidOS uniformly.
            <span className="block text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow/70 mt-1" data-testid="font-scale-sync-status">
              {syncedToServer
                ? "Synced to your profile · follows you across devices"
                : "Stored on this device (sign in to sync across devices)"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={decrease}
            disabled={scale <= FONT_MIN + 1e-6}
            className="rounded-none border-primary/40 h-9 w-9 p-0"
            aria-label="Decrease reading size"
            data-testid="font-scale-decrease"
          >
            <Minus size={14} weight="bold" />
          </Button>
          <div
            className="min-w-[72px] text-center font-display font-bold text-lg tabular-nums"
            data-testid="font-scale-value"
            aria-live="polite"
          >
            {pct}%
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={increase}
            disabled={scale >= FONT_MAX - 1e-6}
            className="rounded-none border-primary/40 h-9 w-9 p-0"
            aria-label="Increase reading size"
            data-testid="font-scale-increase"
          >
            <Plus size={14} weight="bold" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={reset}
            disabled={Math.abs(scale - 1.0) < 1e-6}
            className="rounded-none border-primary/40 h-9"
            data-testid="font-scale-reset"
          >
            <ArrowCounterClockwise size={12} weight="bold" className="mr-1" /> Reset
          </Button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            onClick={() => setScale(p.value)}
            className={`text-[0.625rem] font-mono uppercase tracking-widest px-3 py-1.5 border transition ${
              Math.abs(scale - p.value) < FONT_STEP / 2
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border/60 text-muted-foreground hover:border-primary hover:text-primary"
            }`}
            data-testid={`font-preset-${p.label.toLowerCase()}`}
          >
            {p.label} · {Math.round(p.value * 100)}%
          </button>
        ))}
      </div>

      {/* Live sample so the partner can eyeball the change without switching tabs. */}
      <div className="mt-4 border-l-2 border-primary/40 pl-4 py-2">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Preview</div>
        <div className="mt-1 font-display text-2xl font-bold tracking-tight">Good afternoon, J.</div>
        <div className="mt-1 text-sm text-muted-foreground">Your strategic pulse across clients, engagements, pipeline, and team capacity.</div>
      </div>
    </div>
  );
}
